import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { API } from "@/data/site";

const DUOTONE_SLUGS = new Set([
  "postpartum-care-companion",
  "the-first-forty-days",
  "cesarean-remediation",
  "nurtured-closing-ceremony",
]);

// Show the whole photo (no vertical crop) for tall portrait imagery
const CONTAIN_SLUGS = new Set([]);

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((res) => setServices(res.data.filter((s) => s.slug !== "planning-for-the-4th-trimester" && s.slug !== "the-first-forty-days")))
      .catch(() => {});
  }, []);

  return (
    <section id="services" className="bg-ivory py-24 lg:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <FadeUp className="lg:col-span-7">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sienna">
              Also available · In-home care
            </p>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-espresso sm:text-5xl lg:text-6xl">
              More ways to be <span className="italic text-sienna">held</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="flex items-end lg:col-span-5">
            <p className="max-w-sm text-base leading-relaxed text-cocoa">
              Beyond our gathering, I carry the traditional care into your home. Each visit
              answers one question only: what does the mother need today?
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 gap-x-10 gap-y-16 md:grid-cols-2">
          {services.map((s, i) => (
            <FadeUp key={s.id} delay={0.05 * (i % 2)}>
              {s.coming_soon ? (
                <div className="group block" data-testid={`service-card-${s.slug}`}>
                  <div className="relative overflow-hidden rounded-xl border border-espresso/15 bg-cream">
                    <img
                      src={s.image}
                      alt={s.title}
                      className={`aspect-[4/3] w-full opacity-80 ${CONTAIN_SLUGS.has(s.slug) ? "object-contain" : "object-cover"} ${DUOTONE_SLUGS.has(s.slug) ? "duotone-warm" : ""}`}
                    />
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-sm italic text-sienna">{String(i + 1).padStart(2, "0")}</p>
                      <h3 className="mt-1 font-serif text-2xl font-medium text-espresso lg:text-3xl">{s.title}</h3>
                      <span className="mt-3 inline-block rounded-full bg-sienna px-4 py-1.5 text-xs font-medium uppercase tracking-[0.15em] text-ivory" data-testid={`coming-soon-badge-${s.slug}`}>
                        Coming soon
                      </span>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-cocoa">{s.tagline}</p>
                      <p className="mt-2 max-w-md text-[13px] leading-relaxed text-cocoa/70">{s.description}</p>
                      <p className="mt-3 text-xs font-medium uppercase tracking-[0.18em] text-cocoa/70">
                        {s.duration}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={`/book?service=${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block"
                >
                  <div className="overflow-hidden rounded-xl border border-espresso/15 bg-cream">
                    <img
                      src={s.image}
                      alt={s.title}
                      className={`aspect-[4/3] w-full transition-transform duration-700 ease-out group-hover:scale-105 ${CONTAIN_SLUGS.has(s.slug) ? "object-contain" : "object-cover"} ${DUOTONE_SLUGS.has(s.slug) ? "duotone-warm" : ""}`}
                    />
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-sm italic text-sienna">{String(i + 1).padStart(2, "0")}</p>
                      <h3 className="mt-1 font-serif text-2xl font-medium text-espresso lg:text-3xl">{s.title}</h3>
                      <p className="mt-2 max-w-md text-sm leading-relaxed text-cocoa">{s.tagline}</p>
                      <p className="mt-2 max-w-md text-[13px] leading-relaxed text-cocoa/70">{s.description}</p>
                      <p className="mt-3 text-xs font-medium uppercase tracking-[0.18em] text-cocoa/70">
                        {s.duration} · {s.price}
                      </p>
                      {s.note && (
                        <p className="mt-2 font-serif text-sm italic text-sienna" data-testid={`service-note-${s.slug}`}>
                          {s.note}
                        </p>
                      )}
                    </div>
                    <span className="mt-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-espresso/20 text-espresso transition-all duration-300 group-hover:bg-sienna group-hover:text-ivory group-hover:border-sienna">
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              )}
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <div className="mt-20 max-w-3xl border-t border-espresso/15 pt-8" data-testid="services-disclaimer">
            <p className="text-sm leading-relaxed text-cocoa/80">
              Our offerings evolve with the seasons of our practice, and services may be
              added or retired at any time. Traditional bodywork and ritual services —
              including vaginal steaming, womb lifting and belly binding — are ancestral
              practices offered for comfort and wellbeing; please consult your physician
              or midwife before booking to ensure they are right for you.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
